import React, { useState } from "react";
import { RefreshCw, PackageSearch, CheckCircle2, ShoppingCart, PackagePlus, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Panel } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { DataTable, StatCard, TabBar, TableSkeleton, platformBadge, timeStr, PLATFORM_FILTER } from "./shared";

export default function PendingView({ write, navigate }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("not_keep");
  const [autoing, setAutoing] = useState("");

  const sync = async () => {
    setLoading(true);
    try {
      const r = await api.post("/desty/sync-pending", {});
      setData(r.data);
      const s = r.data.summary || {};
      toast.success(`${s.total_pending || 0} perlu dikirim · ${s.not_keep || 0} belum di Keep · ${s.in_sale || 0} sudah di kasir`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const autoKeep = async (o) => {
    setAutoing(o.order_sn);
    try {
      const r = await api.get("/desty/keep-draft", { params: { order_id: o.order_id } });
      navigate("/sales/keep/create", { state: { destyDraft: {
        marketplace: o.platform, order_id: o.order_sn, items: r.data.items || [],
      } } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setAutoing("");
    }
  };

  const s = data?.summary || {};
  const rows = data ? (data[tab] || []) : [];
  const TABS = [
    { key: "not_keep", label: "Belum Diproses ", icon: PackageSearch, count: data?.not_keep?.length || 0 },
    { key: "in_keep", label: "Sudah di Keep ", icon: CheckCircle2, count: data?.in_keep?.length || 0 },
    { key: "in_sale", label: "Sudah di Kasir ", icon: ShoppingCart, count: data?.in_sale?.length || 0 },
  ];

  const columns = [
    { key: "order_sn", header: "Order ID (Desty)", className: "font-mono text-xs", sortAccessor: (o) => o.order_sn },
    { key: "platform", header: "Platform", render: (o) => platformBadge(o.platform) },
    { key: "store_name", header: "Toko", className: "text-sm", render: (o) => o.store_name || "-" },
    { key: "create_time", header: "Tgl Order", className: "text-sm", sortAccessor: (o) => o.create_time || 0, render: (o) => timeStr(o.create_time) },
    { key: "status", header: "Status Keep", render: (o) => (
      o.keep ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Sudah di Keep · {o.keep.number}</Badge>
        : o.sale_number ? <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Sudah di Kasir · {o.sale_number}</Badge>
        : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Belum diproses</Badge>
    ) },
    { key: "aksi", header: "Aksi", align: "right", render: (o) => (
      !o.keep && !o.sale_number && write ? (
        <Button size="sm" onClick={() => autoKeep(o)} disabled={autoing === o.order_sn}
          className="bg-cocoa hover:bg-cocoa-dark text-ivory h-8" data-testid={`desty-autokeep-${o.order_sn}`}>
          <PackagePlus className="w-3.5 h-3.5 mr-1" /> {autoing === o.order_sn ? "Memuat..." : "Auto Keep"}
        </Button>
      ) : null
    ) },
  ];

  return (
    <>
      <div className="flex justify-end">
        <Button onClick={sync} disabled={loading} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="desty-pending-sync-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Menarik data..." : "Sync Perlu Dikirim"}
        </Button>
      </div>

      {loading && <TableSkeleton testid="desty-pending-skeleton" />}

      {!loading && !data && (
        <Panel className="p-10 text-center text-cocoa/60" data-testid="desty-pending-empty">
          <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Klik <b>Sync Perlu Dikirim</b> untuk menarik order yang belum dikirim dari Desty.
        </Panel>
      )}

      {!loading && data && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total Perlu Dikirim" value={s.total_pending} tone="cocoa" />
            <StatCard label="Belum Diproses" value={s.not_keep} tone="amber" />
            <StatCard label="Sudah di Keep" value={s.in_keep} tone="green" />
            <StatCard label="Sudah di Kasir" value={s.in_sale} tone="slate" />
          </div>
          <TabBar tabs={TABS} value={tab} onChange={setTab} prefix="desty-ptab" />
          <DataTable testid="desty-pending-table" columns={columns} rows={rows}
            searchAccessors={[(o) => o.order_sn, (o) => o.store_name, (o) => o.keep?.number, (o) => o.sale_number]}
            searchPlaceholder="Cari Order ID / toko / no keep…" filters={[PLATFORM_FILTER]}
            emptyText="Tidak ada data pada kategori ini." />
        </div>
      )}
    </>
  );
}
